import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAppSelector } from '@/store';
import { selectIsAuthenticated } from '@/store/authSlice';
import { AUTH_CONFIG } from '@/utils/constants';

// Socket events types
export interface SessionState {
  sessionId: string;
  currentTime: number;
  isPlaying: boolean;
  hostId: string;
  isHost: boolean;
}

export interface PlaybackUpdate {
  sessionId: string;
  isPlaying: boolean;
  currentTime: number;
  timestamp: number;
}

export interface UserJoinedEvent {
  userId: string;
  user: {
    id: string;
    displayName: string;
    avatarUrl?: string;
  } | null;
}

export interface UserLeftEvent {
  userId: string;
  displayName: string;
}

export interface SessionError {
  message: string;
}

// Socket context type
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  // Session actions
  joinSession: (sessionId: string) => void;
  leaveSession: (sessionId: string) => void;
  play: (sessionId: string, currentTime: number) => void;
  pause: (sessionId: string, currentTime: number) => void;
  seek: (sessionId: string, currentTime: number) => void;
  requestSync: (sessionId: string) => void;
  // Event listeners
  onSessionState: (callback: (state: SessionState) => void) => () => void;
  onPlaybackUpdate: (callback: (update: PlaybackUpdate) => void) => () => void;
  onUserJoined: (callback: (event: UserJoinedEvent) => void) => () => void;
  onUserLeft: (callback: (event: UserLeftEvent) => void) => () => void;
  onSessionError: (callback: (error: SessionError) => void) => () => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Initialize socket connection
  useEffect(() => {
    // Get token from localStorage
    const accessToken = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);

    if (!isAuthenticated || !accessToken) {
      // Disconnect if not authenticated
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Get WebSocket URL from environment or default
    // Remove /api/v1 or /api from the API URL to get the base URL for WebSocket
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
    const wsUrl = import.meta.env.VITE_WS_URL || apiUrl.replace(/\/api\/v1$/, '').replace(/\/api$/, '');

    const newSocket = io(wsUrl, {
      auth: {
        token: accessToken,
      },
      transports: ['polling'], // Use only polling to avoid Permissions API issues
      upgrade: false, // Disable upgrade to websocket
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      forceNew: true,
      timeout: 20000,
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated]);

  // Session actions
  const joinSession = useCallback(
    (sessionId: string) => {
      if (socket && isConnected) {
        socket.emit('session:join', { sessionId });
      }
    },
    [socket, isConnected]
  );

  const leaveSession = useCallback(
    (sessionId: string) => {
      if (socket && isConnected) {
        socket.emit('session:leave', { sessionId });
      }
    },
    [socket, isConnected]
  );

  const play = useCallback(
    (sessionId: string, currentTime: number) => {
      if (socket && isConnected) {
        socket.emit('session:play', { sessionId, currentTime, isPlaying: true });
      }
    },
    [socket, isConnected]
  );

  const pause = useCallback(
    (sessionId: string, currentTime: number) => {
      if (socket && isConnected) {
        socket.emit('session:pause', { sessionId, currentTime, isPlaying: false });
      }
    },
    [socket, isConnected]
  );

  const seek = useCallback(
    (sessionId: string, currentTime: number) => {
      if (socket && isConnected) {
        socket.emit('session:seek', { sessionId, currentTime });
      }
    },
    [socket, isConnected]
  );

  const requestSync = useCallback(
    (sessionId: string) => {
      if (socket && isConnected) {
        socket.emit('session:sync', { sessionId });
      }
    },
    [socket, isConnected]
  );

  // Event listener helpers
  const onSessionState = useCallback(
    (callback: (state: SessionState) => void) => {
      if (!socket) return () => {};
      socket.on('session:state', callback);
      return () => {
        socket.off('session:state', callback);
      };
    },
    [socket]
  );

  const onPlaybackUpdate = useCallback(
    (callback: (update: PlaybackUpdate) => void) => {
      if (!socket) return () => {};
      socket.on('session:playbackUpdate', callback);
      return () => {
        socket.off('session:playbackUpdate', callback);
      };
    },
    [socket]
  );

  const onUserJoined = useCallback(
    (callback: (event: UserJoinedEvent) => void) => {
      if (!socket) return () => {};
      socket.on('session:userJoined', callback);
      return () => {
        socket.off('session:userJoined', callback);
      };
    },
    [socket]
  );

  const onUserLeft = useCallback(
    (callback: (event: UserLeftEvent) => void) => {
      if (!socket) return () => {};
      socket.on('session:userLeft', callback);
      return () => {
        socket.off('session:userLeft', callback);
      };
    },
    [socket]
  );

  const onSessionError = useCallback(
    (callback: (error: SessionError) => void) => {
      if (!socket) return () => {};
      socket.on('session:error', callback);
      return () => {
        socket.off('session:error', callback);
      };
    },
    [socket]
  );

  const value: SocketContextType = {
    socket,
    isConnected,
    joinSession,
    leaveSession,
    play,
    pause,
    seek,
    requestSync,
    onSessionState,
    onPlaybackUpdate,
    onUserJoined,
    onUserLeft,
    onSessionError,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

// Hook to use socket context
export function useSocket(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export default SocketContext;
